import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Minus, Plus, Trash2, ArrowRight, ShoppingBag, X, Store } from "lucide-react";
import { Sheet, SheetContent, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { useOrderList } from "@/context/OrderListContext";
import { useSite } from "@/lib/api";
import QuantityPicker from "@/components/order/QuantityPicker";
import CheckoutForm from "@/components/order/CheckoutForm";
import { QUANTITY_OPTIONS, ADET_OPTIONS, formatPrice, lineEstimate, PICKUP_DISCOUNT_PCT } from "@/lib/site";

export const OrderListPanel = () => {
  const {
    items,
    isOpen,
    setIsOpen,
    removeItem,
    updateQuantity,
    updateNote,
    clear,
    total,
    startCheckout,
    setStartCheckout,
  } = useOrderList();
  const { data: site } = useSite();
  const [step, setStep] = useState("list");
  const pickupSavings = Math.round((total * PICKUP_DISCOUNT_PCT) / 100);

  // panel kapanınca veya liste boşalınca adımı sıfırla
  useEffect(() => {
    if (!isOpen || items.length === 0) setStep("list");
  }, [isOpen, items.length]);

  // Mobil "Gönder" / hızlı checkout → panel açılınca doğrudan checkout adımına geç.
  useEffect(() => {
    if (isOpen && startCheckout && items.length > 0) {
      setStep("checkout");
      setStartCheckout(false);
    }
  }, [isOpen, startCheckout, items.length, setStartCheckout]);

  const stepQty = (item, dir) => {
    const opts = item.kind === "market" ? ADET_OPTIONS : QUANTITY_OPTIONS;
    const idx = opts.indexOf(item.quantity);
    if (idx === -1) {
      updateQuantity(item.slug, opts[0]);
      return;
    }
    const next = Math.min(opts.length - 1, Math.max(0, idx + dir));
    updateQuantity(item.slug, opts[next]);
  };

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetContent
        side="right"
        data-testid="order-list-panel"
        className="flex w-full flex-col border-l-4 border-bordo bg-ink p-0 text-cream sm:max-w-md"
      >
        <SheetTitle className="sr-only">Sipariş Listem</SheetTitle>
        <SheetDescription className="sr-only">Seçtiğiniz ürünleri WhatsApp ile siparişe dönüştürün.</SheetDescription>

        {/* fiş başlığı */}
        <div className="bg-stone-light px-6 py-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <ShoppingBag className="h-5 w-5 text-copper" />
              <div>
                <h2 className="font-serif text-2xl leading-none text-cream">Sipariş Listem</h2>
                <p className="mt-1 font-mono text-[11px] uppercase tracking-wider text-cream/50">{items.length} kalem · Ertan Kasap</p>
              </div>
            </div>
            <button
              data-testid="close-order-list"
              onClick={() => setIsOpen(false)}
              className="flex h-11 w-11 items-center justify-center rounded-sm text-cream/60 transition-colors hover:bg-cream/10 hover:text-cream"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {items.length === 0 ? (
          <div className="flex flex-1 flex-col items-center justify-center px-6 text-center">
            <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full border border-dashed border-copper/40">
              <ShoppingBag className="h-7 w-7 text-copper/60" />
            </div>
            <p className="font-serif text-2xl text-cream">Listeniz henüz boş</p>
            <p className="mt-1 text-sm text-cream/55">Beğendiğiniz ürünleri ekleyin, tek tuşla WhatsApp’tan gönderelim.</p>
            <Link
              to="/urunler"
              onClick={() => setIsOpen(false)}
              className="mt-6 inline-flex items-center gap-2 rounded-sm bg-bordo px-5 py-2.5 text-sm font-medium uppercase tracking-wide text-cream transition-colors hover:bg-bordo-dark"
            >
              Ürünleri İncele
            </Link>
          </div>
        ) : step === "checkout" ? (
          <CheckoutForm
            site={site}
            onBack={() => setStep("list")}
            onDone={() => { clear(); setIsOpen(false); setStep("list"); }}
          />
        ) : (
          <>
            <div className="flex-1 space-y-3 overflow-y-auto px-4 py-4">
              {items.map((item) => (
                <div
                  key={item.slug}
                  data-testid={`order-item-${item.slug}`}
                  className="border border-dashed border-copper/25 bg-stone p-3"
                >
                  <div className="flex gap-3">
                    <img src={item.image} alt={item.name} className="h-16 w-16 flex-shrink-0 object-cover" />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-start justify-between gap-2">
                        <Link
                          to={item.kind === "market" ? "/market" : `/urun/${item.slug}`}
                          onClick={() => setIsOpen(false)}
                          className="truncate font-serif text-lg leading-tight text-cream transition-colors hover:text-copper"
                        >
                          {item.name}
                        </Link>
                        <button
                          data-testid={`remove-item-${item.slug}`}
                          onClick={() => removeItem(item.slug)}
                          className="-mr-2 -mt-1.5 flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-sm text-cream/35 transition-colors hover:text-copper"
                          aria-label={`${item.name} ürününü listeden çıkar`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>

                      <div className="mt-2 flex items-center gap-2">
                        <button
                          data-testid={`decrease-${item.slug}`}
                          onClick={() => stepQty(item, -1)}
                          className="flex h-11 w-11 flex-shrink-0 items-center justify-center border border-copper/40 text-copper transition-colors hover:bg-bordo hover:text-cream"
                          aria-label="Miktarı azalt"
                        >
                          <Minus className="h-4 w-4" />
                        </button>
                        <QuantityPicker
                          testid={`quantity-${item.slug}`}
                          value={item.quantity}
                          options={item.kind === "market" ? ADET_OPTIONS : QUANTITY_OPTIONS}
                          onChange={(q) => updateQuantity(item.slug, q)}
                        />
                        <button
                          data-testid={`increase-${item.slug}`}
                          onClick={() => stepQty(item, 1)}
                          className="flex h-11 w-11 flex-shrink-0 items-center justify-center border border-copper/40 text-copper transition-colors hover:bg-bordo hover:text-cream"
                          aria-label="Miktarı artır"
                        >
                          <Plus className="h-4 w-4" />
                        </button>
                      </div>

                      {item.price != null && (
                        <div className="mt-2.5 flex items-center justify-between">
                          <span className="font-mono text-[11px] text-cream/45">
                            {formatPrice(item.price)} / {item.unit || "kg"}
                          </span>
                          {lineEstimate(item) != null && (
                            <span
                              data-testid={`line-total-${item.slug}`}
                              className="font-mono text-sm font-bold text-copper"
                            >
                              ≈ {formatPrice(Math.round(lineEstimate(item)))}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  <Input
                    data-testid={`note-${item.slug}`}
                    value={item.note}
                    onChange={(e) => updateNote(item.slug, e.target.value)}
                    placeholder="Not ekle (örn. ince kıyılsın)"
                    className="mt-3 h-9 rounded-sm border-copper/20 bg-stone-light font-mono text-sm text-cream placeholder:text-cream/30"
                  />
                </div>
              ))}

              <button
                data-testid="clear-order-list"
                onClick={clear}
                className="mt-2 w-full text-center font-mono text-[11px] uppercase tracking-wider text-cream/40 transition-colors hover:text-copper"
              >
                Listeyi temizle
              </button>
            </div>

            <div className="border-t border-dashed border-copper/30 bg-stone-light px-5 py-5">
              {total > 0 && (
                <div
                  data-testid="order-estimated-total"
                  className="mb-4 flex items-baseline justify-between border-b border-dashed border-copper/25 pb-4"
                >
                  <span className="font-mono text-xs uppercase tracking-wider text-cream/60">Tahmini Toplam</span>
                  <span className="font-serif text-3xl leading-none text-copper">{formatPrice(Math.round(total))}</span>
                </div>
              )}
              {total > 0 && (
                <div
                  data-testid="pickup-discount-hint"
                  className="mb-4 flex items-center gap-3 rounded-sm border border-copper/45 bg-gradient-to-r from-copper/20 via-copper/[0.06] to-transparent px-4 py-3"
                >
                  <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full border border-copper/50 bg-copper/10">
                    <Store className="h-5 w-5 text-copper" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm leading-tight text-cream">
                      Dükkândan alırsanız <span className="font-bold text-copper">%{PICKUP_DISCOUNT_PCT} indirim</span>
                    </p>
                    <p className="mt-0.5 font-mono text-[11px] uppercase tracking-wider text-copper/85">
                      ≈ {formatPrice(pickupSavings)} tasarruf
                    </p>
                  </div>
                </div>
              )}
              <button
                data-testid="proceed-to-checkout"
                onClick={() => setStep("checkout")}
                className="flex w-full items-center justify-center gap-2.5 rounded-sm bg-[#25D366] px-5 py-3.5 text-base font-bold uppercase tracking-wide text-charcoal transition-colors hover:bg-[#1eb858]"
              >
                Devam Et · Sipariş Bilgileri <ArrowRight className="h-5 w-5" />
              </button>
              <p className="mt-3 text-center font-mono text-[11px] text-cream/45">
                Ad, adres ve ödeme bilgisi · sonra WhatsApp'tan gönderilir
              </p>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default OrderListPanel;
